import { useState } from 'react';
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Select, SelectItem, Input,
} from '@heroui/react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useProjectMaterials, useCreateProjectMaterial, useUpdateProjectMaterial, useDeleteProjectMaterial } from '@/api/project-materials';
import { useMaterialsLibrary } from '@/api/materials-library';

const SPEC_CODES = [
  { code: 'box_panel_spec', label: 'Box Panel', group: 'Cabinet Body' },
  { code: 'back_panel_spec', label: 'Back Panel', group: 'Cabinet Body' },
  { code: 'stretcher_spec', label: 'Stretcher', group: 'Cabinet Body' },
  { code: 'shelf_spec', label: 'Shelf', group: 'Cabinet Body' },
  { code: 'door_spec', label: 'Door', group: 'Face' },
  { code: 'drawer_box_spec', label: 'Drawer Box', group: 'Face' },
  { code: 'edgeband_spec', label: 'Edgeband', group: 'Trim' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

export default function ProjectMaterialsModal({ isOpen, onClose, projectId }: Props) {
  const { data: assignments = [] } = useProjectMaterials(projectId);
  const { data: materials = [] } = useMaterialsLibrary();
  const createMutation = useCreateProjectMaterial();
  const updateMutation = useUpdateProjectMaterial();
  const deleteMutation = useDeleteProjectMaterial();
  const [addingSpec, setAddingSpec] = useState('');

  const assignmentMap = new Map((assignments as any[]).map((a) => [a.spec_code, a]));

  const handleAssign = async (specCode: string, materialId: string) => {
    const existing = assignmentMap.get(specCode);
    try {
      if (existing) {
        await updateMutation.mutateAsync({ id: existing.id, material_id: materialId });
      } else {
        const spec = SPEC_CODES.find((s) => s.code === specCode);
        await createMutation.mutateAsync({
          project_id: projectId,
          spec_code: specCode,
          material_id: materialId,
          material_group: spec?.group ?? 'General',
        });
      }
      toast.success('Material assigned');
    } catch {
      toast.error('Failed to assign material');
    }
  };

  const handleRemove = async (specCode: string) => {
    const existing = assignmentMap.get(specCode);
    if (!existing) return;
    try {
      await deleteMutation.mutateAsync(existing.id);
      toast.success('Material assignment removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside"
      classNames={{
        base: 'bg-[var(--bg-surface)] text-[var(--text-primary)]',
        header: 'border-b border-[var(--border)]',
        footer: 'border-t border-[var(--border)]',
      }}
    >
      <ModalContent>
        <ModalHeader>Project Material Defaults</ModalHeader>
        <ModalBody>
          <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
            Set default materials for each spec code. These apply project-wide unless overridden at area or item level.
          </p>
          <div className="space-y-2">
            {SPEC_CODES.map(({ code, label, group }) => {
              const assignment = assignmentMap.get(code);
              return (
                <div key={code} className="flex items-center gap-2 p-2 rounded" style={{ backgroundColor: 'var(--bg-panel)' }}>
                  <div className="w-32 flex-shrink-0">
                    <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{label}</div>
                    <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{group}</div>
                  </div>
                  <Select
                    aria-label={`Material for ${label}`}
                    selectedKeys={assignment?.material_id ? [assignment.material_id] : []}
                    onSelectionChange={(keys) => {
                      const val = Array.from(keys)[0] as string;
                      if (val) handleAssign(code, val);
                    }}
                    size="sm"
                    className="flex-1"
                    placeholder="Not set"
                    classNames={{ trigger: 'bg-[var(--bg-surface)] border-[var(--border)]' }}
                  >
                    {(materials as any[]).map((m) => (
                      <SelectItem key={m.id}>{m.name}</SelectItem>
                    ))}
                  </Select>
                  {assignment && (
                    <button onClick={() => handleRemove(code)} className="p-1 rounded hover:bg-[var(--bg-hover)]" title="Remove">
                      <Trash2 size={14} style={{ color: 'var(--text-muted)' }} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="bordered" onPress={onClose} className="border-[var(--border)] text-[var(--text-secondary)]">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
